import React from 'react';
import useConstant from 'use-constant';
import awesomeDebouncePromise from 'awesome-debounce-promise';
import {useAsync} from 'react-async';

import * as S from './HomePageAutosuggest.styles';

import {getPlacesId} from './utils';
import {itemToString} from '../utils';

import {DEFAULT_ERROR_MESSAGE, DEBOUNCE_TIMEOUT} from '../constants';

import Text from '../../../../../core-components/TextFieldExpt/TextFieldExpt';

import TripAdvisorIcon from '../../../../../../stories/Icons/TripAdvisorIcon';

import {searchWidgetLocationSearchURL} from '../../../../../../shared/config/app-config';

import useAutosuggest from '../../../../../../shared/hooks/useAutosuggest';
import TrendingDestinations from '../../../../../../shared/pages/HomePage/containers/TrendingDestinations';
import AutosuggestIconSelector from '../../../../../../shared/components/AutosuggestIconSelector';

const fetchVoyagerLocationSuggestions = async ({inputValue, partner}, {signal}) => {
  if (!inputValue) {
    return [];
  }

  const fetchVoyagerUrlForInput = searchWidgetLocationSearchURL(inputValue, partner);
  const res = await fetch(fetchVoyagerUrlForInput, {signal});
  if (!res.ok) throw new Error(res.statusText);
  const ret = await res.json();

  if (ret.data && ret.data.r) {
    return ret.data.r;
  }

  /** 🐨 handle gracefully if voyager doesn't return ret.data.r an array */
};

function HomePageAutosuggest({
  initialInputValue,
  onItemSelection,
  toggleOverlay,
  partner,
  onClick: onClickFromProps,
}) {
  const {
    propGetters: {getInputProps, getMenuProps, getItemProps, getRootProps, getLabelProps},
    state: {isOpen, highlightedIndex, selectedItem, inputValue},
    actions: {openMenu},
  } = useAutosuggest({
    itemToString,
    initialInputValue,
    async onSelectedItemChange(changes) {
      let updatedSuggestion = changes.selectedItem || {};

      if (!changes.selectedItem._id) {
        try {
          updatedSuggestion = await getPlacesId(changes.selectedItem);
        } catch (err) {
          console.info(err);
        }
      }

      onItemSelection({
        ...changes,
        selectedItem: updatedSuggestion,
      });

      toggleOverlay(false);
    },
  });

  const debouncedVoyagerFetchPromise = useConstant(() =>
    awesomeDebouncePromise(fetchVoyagerLocationSuggestions, DEBOUNCE_TIMEOUT),
  );

  // @ts-ignore
  const {data: items = [], isPending, isRejected, error} = useAsync({
    promiseFn: debouncedVoyagerFetchPromise,
    watch: inputValue,
    inputValue,
    partner,
  });

  const inputRef = React.useRef(null);
  const [menuHeight, setMenuHeight] = React.useState(null);

  React.useEffect(() => {
    if (inputRef.current && isOpen) {
      const distanceBtwInputAndWindowBottom =
        window.innerHeight -
        (inputRef.current.getBoundingClientRect().y + inputRef.current.offsetHeight);

      const proportionalDistance = distanceBtwInputAndWindowBottom / window.innerHeight;
      const percentageDistance = proportionalDistance * 100;
      const factorOfReductionForMenuHeight = percentageDistance * 0.625;

      const menuHeight = factorOfReductionForMenuHeight;

      setMenuHeight(menuHeight);
    }
  }, [isOpen]);

  return (
    // @ts-ignore
    <S.AutocompleteWrapperStyles {...getRootProps()}>
      <S.SearchInputStyles
        {...getInputProps({
          // @ts-ignore
          ref: inputRef,
          placeholder: 'City Name / Hotel Name',
          onFocus: () => {
            toggleOverlay(true);
            if (!items || !items.length) {
              openMenu();
            }
          },
          onBlur: () => {
            toggleOverlay(false);
          },
          onClick: () => {
            onClickFromProps();
          },
        })}
      />

      {isRejected && (error || DEFAULT_ERROR_MESSAGE) && (
        <S.ErrorMessage>Enter Location</S.ErrorMessage>
      )}

      <S.SearchMenuStyles
        {...getMenuProps()}
        maxHeight={`${menuHeight}vh`}
        // @ts-ignore
      >
        {isOpen && items.length > 0
          ? items.map((item, index) => {
              const key = item._id ? `${item._id}${index}` : item.n ? `${item._n}${index}` : index;

              return (
                <S.SearchListItem
                  // @ts-ignore
                  {...getItemProps({
                    item,
                    index,
                    isActive: highlightedIndex === index,
                    isSelected: selectedItem === item,
                  })}
                  /** 🐨tell voyager about below */
                  /** 🐨take 'identifier' from the user of the Autosuggest component
                   * this shouldn't be hardcoded
                   */
                  key={key} // fallback because voyager sends _id = null sometimes
                >
                  <S.StyledIcon
                  // @ts-ignore
                  >
                    <AutosuggestIconSelector name={item.t} />
                  </S.StyledIcon>
                  <S.SearchListItemLocationName>{itemToString(item)}</S.SearchListItemLocationName>
                  <S.SearchListItemLocationType>
                    <div>{item.t || null}</div>
                    {item.xtr && item.xtr.g_rtg && (
                      <S.RatingStyles rating={item.xtr.g_rtg}>
                        {/* gri === 3 is triadvisor */}
                        {item.xtr && item.xtr.gri === 3 && (
                          // 🐨 use <Image /> component when its ready
                          <TripAdvisorIcon />
                        )}
                        <Text inline customFontSize="1.4">
                          {item.xtr.g_rtg} / 5
                        </Text>
                      </S.RatingStyles>
                    )}
                    {(item.nh && item.nh > 0 && (
                      <S.SearchHotelNumber>{item.nh} Hotels</S.SearchHotelNumber>
                    )) ||
                      null}
                  </S.SearchListItemLocationType>
                </S.SearchListItem>
              );
            })
          : null}

        {(!inputValue && isOpen) ? <TrendingDestinations /> : null}
      </S.SearchMenuStyles>
    </S.AutocompleteWrapperStyles>
  );
}

export default HomePageAutosuggest;
